import { AdditionalFeature, AssignedProjectFeature } from "@app/project/model/project-feature";
import { ActionPayloadGeneric } from "@app/root-store/actions";
import { createActionSet, createCrudActionSets, createNoPropsAction } from "@app/root-store/actions/action-creators";

/**
 * @description Used in the action creators to generate an action type like:
 * "[Project Optional Settings Management] Load Project Optional Settings"
 */
const actionCategory = "Project Feature";

/**
 * A payload used when performing an operation on a project optional settings
 */
export type ActionPayload = ActionPayloadGeneric;

/**
 * A payload used when loading a project optional settings
 */
interface LoadActionPayload {
    projectId: number;
}

/**
 * An action result that returns a single project optional settings
 */
export interface ActionResult {
    payload: AssignedProjectFeature[];
}

/**
 * An action result that returns a collection of projects optional settings
 */
export interface BulkActionResult {}

/**
 * Create the CRUD set of actions associated with the action category.
 * Must send in appropriate payload types for a single and load and result types for single and bulk.
 * A request, success and failure action are created for each CRUD operation.
 * Each action set should be deconstructed into consts from the result.
 */
export const {
    load: loadSet,
    create: createSet,
    update: updateSet,
    delete: deleteSet,
} = createCrudActionSets<ActionPayload, LoadActionPayload, ActionResult, BulkActionResult>(actionCategory);

/**
 * Create each action from the deconstructed action sets created by createCrudActionSets.
 * Each action is strongly typed and should be used in accordance with the NgRx docs.
 * Any action is optional and can be omitted by simply not desctructuring it.
 */
export const { request: load, success: loadSuccess, failure: loadFailure } = loadSet;
export const { request: create, success: createSuccess, failure: createFailure } = createSet;
export const { request: update, success: updateSuccess, failure: updateFailure } = updateSet;
export const { request: deleteItem, success: deleteSuccess, failure: deleteFailure } = deleteSet;

export interface ProjectFeatureActionResult {
    payload: {
        [key: string]: AdditionalFeature;
    };
}
export const loadAllAdditionalFeatures = createActionSet<unknown, ProjectFeatureActionResult>(actionCategory, "Load All");
export const clear = createNoPropsAction(actionCategory, "Clear State");
